import { sum } from "mathjs";
import { DATA_TYPES } from "../constants";

const argFact = (compareFn) => (array: number[]) =>
  array.map((el, idx) => [el, idx]).reduce(compareFn)[1];

export const argMax = argFact((min: number[], el: number[]) =>
  el[0] > min[0] ? el : min
);
export const argMin = argFact((max: number[], el: number[]) =>
  el[0] < max[0] ? el : max
);

export function logitsToNumber(data: number[][]) {
  let argmax: number[] = [];
  for (let ar of data) {
    argmax.push(ar.indexOf(Math.max.apply(null, ar)));
  }
  return argmax;
}

export function to1DArray(data: number[][]) {
  let ar: number[] = [];
  const nRows = data.length;
  const nCols = data[0].length;
  if (nRows === 1) {
    ar = data[0];
  } else if (nCols === 1) {
    ar = data.map((val) => val[0]);
  }
  return ar;
}

export function to2DArray(data: number[]) {
  let ar: number[][] = [];
  ar = data.map((val) => [val]);
  return ar;
}
export function safeTrim(val: string) {
  if (val === undefined) {
    return "";
  }
  if (typeof val !== "string") {
    return val;
  }
  return val.trim();
}
export const isNumber = (val: string) => {
  return !isNaN2(convertToNumber(val));
};
export const convertToNumber = (val: string) => {
  if (typeof val === "number") {
    return val;
  }
  if (typeof val !== "string") {
    return NaN;
  }
  val = val.replaceAll("$", "").replaceAll(",", "");

  return val === "" ? NaN : +val;
};
export const isNaN2 = (val: number) => {
  return val === null || isNaN(val);
};
export const calcAverage = (data: number[], colType: string) => {
  if (colType == DATA_TYPES.categorical || colType == DATA_TYPES.unusable) {
    return NaN;
  }
  const denominator = data.length > 0 ? data.length : 1;
  return data.reduce((a, b) => a + b, 0) / denominator;
};

export const calcPercentile = (
  data: number[],
  percentile: number,
  colType: string
) => {
  if (colType == DATA_TYPES.categorical || colType == DATA_TYPES.unusable) {
    return NaN;
  }
  let m: number[] = [];
  for (let val of data) {
    if (!isNaN2(val)) {
      m.push(val);
    }
  }
  // extract the values field and sort the resulting array
  m.sort(function (a, b) {
    return a - b;
  });

  var index = Math.floor((m.length - 1) * percentile);
  return m[index];
};
export const calcMin = (data: number[], columnType: string) => {
  if (
    columnType == DATA_TYPES.categorical ||
    columnType == DATA_TYPES.unusable
  ) {
    return NaN;
  }
  let minVal: number = data[0];
  for (let val of data) {
    if (val < minVal) {
      minVal = val;
    }
  }
  return minVal;
};
export const calcMax = (data: number[], columnType: string) => {
  if (
    columnType == DATA_TYPES.categorical ||
    columnType == DATA_TYPES.unusable
  ) {
    return NaN;
  }
  let maxVal: number = data[0];
  for (let val of data) {
    if (val > maxVal) {
      maxVal = val;
    }
  }
  return maxVal;
};

export type boundaryType = {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
};
export function findBoundaries(X: number[][]): boundaryType {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  for (let x of X) {
    minX = x[0] < minX ? x[0] : minX;
    minY = x[1] < minY ? x[1] : minY;
    maxX = x[0] > maxX ? x[0] : maxX;
    maxY = x[1] > maxY ? x[1] : maxY;
  }
  return {
    minX: minX,
    minY: minY,
    maxX: maxX,
    maxY: maxY,
  };
}

export function addAlpha(color: string, opacity: number): string {
  // coerce values so ti is between 0 and 1.
  const _opacity = Math.round(Math.min(Math.max(opacity || 1, 0), 1) * 255);
  return color + _opacity.toString(16).toUpperCase();
}

export function calcF1Score(y2: number[], pred2: number[]) {
  let y: number[] = [];
  let pred: number[] = [];
  for (let i = 0; i < y2.length; i++) {
    if (isNaN2(y2[i]) || isNaN2(pred2[i])) {
      continue;
    }
    y.push(y2[i]);
    pred.push(pred2[i]);
  }

  let uniqueItems = Array.from(new Set(y)).sort();
  let TP = Array(uniqueItems.length).fill(0);
  let FP = Array(uniqueItems.length).fill(0);
  let FN = Array(uniqueItems.length).fill(0);
  let counts = Array(uniqueItems.length).fill(0);
  for (let i = 0; i < uniqueItems.length; i++) {
    for (let j = 0; j < y.length; j++) {
      if (y[j] === uniqueItems[i]) {
        counts[i] += 1;
      }
      if (y[j] === uniqueItems[i] && pred[j] === uniqueItems[i]) {
        TP[i] += 1;
      }
      if (y[j] !== uniqueItems[i] && pred[j] === uniqueItems[i]) {
        FP[i] += 1;
      }
      if (y[j] === uniqueItems[i] && pred[j] !== uniqueItems[i]) {
        FN[i] += 1;
      }
    }
  }
  let precissions = Array(uniqueItems.length).fill(0);
  for (let i = 0; i < precissions.length; i++) {
    if (TP[i] + FP[i] === 0) {
      precissions[i] = 0;
    } else {
      precissions[i] = TP[i] / (TP[i] + FP[i]);
    }
  }
  let recalls = Array(uniqueItems.length).fill(0);
  for (let i = 0; i < recalls.length; i++) {
    if (TP[i] + FN[i] === 0) {
      recalls[i] = 0;
    } else {
      recalls[i] = TP[i] / (TP[i] + FN[i]);
    }
  }
  let F1s = Array(uniqueItems.length).fill(0);
  for (let i = 0; i < recalls.length; i++) {
    if (precissions[i] + recalls[i] === 0) {
      F1s[i] = 0;
    } else {
      F1s[i] =
        ((counts[i] / y.length) * (2 * precissions[i] * recalls[i])) /
        (precissions[i] + recalls[i]);
    }
  }
  return sum(F1s);
}
